import React, { Component } from 'react';
import { circlePolyline } from '../../util/maps';

/**
 * Map that uses Google Maps and is fully dynamic (zoom, pan, etc.).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.containerClassName add style rules for the root container
 * @param {string?} props.mapClassName add style rules for the map div
 * @param {string?} props.address
 * @param {Object} props.center LatLng
 * @param {number} props.center.lat latitude
 * @param {number} props.center.lng longitude
 * @param {number} props.zoom
 * @param {Object} props.mapsConfig
 * @returns {JSX.Element} dynamic version of Google Maps
 */
class DynamicGoogleMap extends Component {
  constructor(props) {
    super(props);
    this.map = null;
    this.marker = null;
    this.mapContainer = null;
    this.initializeMap = this.initializeMap.bind(this);
  }


  componentDidMount(prevProps) {
    if (!this.map && this.mapContainer) {
      this.initializeMap();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.map &&
      (
        prevProps.center.lat !== this.props.center.lat ||
        prevProps.center.lng !== this.props.center.lng
      )
    ) {
      // Move map center
      this.map.setCenter(this.props.center);

      // Remove previous marker if exists
      if (this.marker) {
        this.marker.setMap(null);
      }

      // Add new marker
      this.marker = new window.google.maps.Marker({
        position: this.props.center,
        map: this.map,
        title: this.props.address,
      });
    }
  }



  initializeMap() {
    const { offsetHeight, offsetWidth } = this.mapContainer;
    const hasDimensions = offsetHeight > 0 && offsetWidth > 0;

    if (hasDimensions) {
      const { center, zoom, address, mapsConfig, onLocationSelect } = this.props;
      const maps = window.google.maps;
      const controlPosition = window.google.maps.ControlPosition.LEFT_TOP;

      // const svgIcon = {
      //   url: `
      //  data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNlMjJhMmEiIHN0cm9rZS13aWR0aD0iMS43NSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1waW4taWNvbiBsdWNpZGUtcGluIj48cGF0aCBkPSJNMTIgMTd2NSIvPjxwYXRoIGQ9Ik05IDEwLjc2YTIgMiAwIDAgMS0xLjExIDEuNzlsLTEuNzguOUEyIDIgMCAwIDAgNSAxNS4yNFYxNmExIDEgMCAwIDAgMSAxaDEyYTEgMSAwIDAgMCAxLTF2LS43NmEyIDIgMCAwIDAtMS4xMS0xLjc5bC0xLjc4LS45QTIgMiAwIDAgMSAxNSAxMC43NlY3YTEgMSAwIDAgMSAxLTEgMiAyIDAgMCAwIDAtNEg4YTIgMiAwIDAgMCAwIDQgMSAxIDAgMCAxIDEgMXoiLz48L3N2Zz4=
      //   `,
      //   scaledSize: new maps.Size(36, 36),
      //   anchor: new maps.Point(18, 36),
      // };

      const mapConfig = {
        center,
        zoom,
        // Disable all controls except zoom
        // https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions

        // Disable map type (ie. Satellite etc.)
        mapTypeControl: false,
        // Disable zooming by scrolling
        scrollwheel: true,
        // Fullscreen control toggle
        fullscreenControl: false,
        // Street View control
        streetViewControl: false,
        // Camera control position
        cameraControlOptions: {
          position: controlPosition,
        },
      };

      this.map = new maps.Map(this.mapContainer, mapConfig);

      // Add draggable marker
      this.marker = new maps.Marker({
        position: center,
        map: this.map,
        draggable: true,
        title: address,
        // icon: svgIcon
      });

      // Handle map clicks
      this.map.addListener('click', e => {
        const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };

        if (this.marker) {
          this.marker.setPosition(coords);
        }

        if (e.placeId) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ placeId: e.placeId }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const address = results[0].formatted_address;
              if (onLocationSelect) onLocationSelect(coords, address);
            } else {
              console.warn('Geocoder failed:', status);
              if (onLocationSelect) onLocationSelect(coords);
            }
          });
        } else {
          if (onLocationSelect) onLocationSelect(coords);
        }
      });

      this.marker.addListener('dragend', e => {
        const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: coords }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const address = results[0].formatted_address;
            if (onLocationSelect) onLocationSelect(coords, address);
          } else {
            if (onLocationSelect) onLocationSelect(coords);
          }
        });
      });


      // if (mapsConfig.fuzzy.enabled) {
      if (this.props.isListingPage) {
        const GoogleLatLng = window.google.maps.LatLng;
        // Origin as object literal (LatLngLiteral)
        const origin = { lat: center.lat, lng: center.lng };
        const radius = mapsConfig.fuzzy.offset;
        const path = circlePolyline(origin, radius).map(c => new GoogleLatLng(c[0], c[1]));



        const circleProps = {
          options: {
            fillColor: mapsConfig.fuzzy.circleColor,
            fillOpacity: 0.2,
            strokeColor: mapsConfig.fuzzy.circleColor,
            strokeOpacity: 0.5,
            strokeWeight: 1,
            clickable: false,
          },
          path,
          map: this.map,
        };

        // Add a circle. We use Polygon because the default Circle class is not round enough.
        const Polygon = window.google.maps.Polygon;
        new Polygon(circleProps);
      }
      else {
        // new window.google.maps.Marker({
        //   position: center,
        //   map: this.map,
        //   title: address,
        // });
      }
    }
  }



  render() {
    const { containerClassName, mapClassName } = this.props;
    return (
      <div className={containerClassName}>
        <div className={mapClassName} ref={el => (this.mapContainer = el)} />
      </div>
    );
  }
}

export default DynamicGoogleMap;
